import React from 'react';
import { Amplitude, Events } from '@iwoca/frontend-tracking-library';
import { Modal } from '@iwoca/orion';
import { t } from '@lingui/macro';
import { useIntroducerContext } from 'contexts/IntroducerContext/IntroducerContext';
import { useLocale } from 'hooks/useLocale';
import usePhoneNumber from 'hooks/usePhoneNumber';
import { useSearchParams } from 'react-router-dom';
import { ASSET_PREFIX } from 'utils/assets/assets';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import styles from './HelpModal.module.css';

type Props = {
  showModal: boolean;
  closeModal: () => void;
};

export const HelpModal: React.FunctionComponent<Props> = ({
  showModal,
  closeModal,
}) => {
  const [searchParams] = useSearchParams();
  const { introducerType } = useIntroducerContext();
  const isAccountant =
    introducerType === 'accountant' ||
    searchParams.get('introducerType') === 'accountant';
  const { phoneNumber, phoneNumberLink } = usePhoneNumber(isAccountant);
  const { locale } = useLocale();

  const handlePhoneNumberTracking = () => {
    Amplitude.track(
      Events.introducerPortal.introducerPortalEventCreator({
        event: AMPLITUDE_EVENTS.contactLinkClicked,
        locale,
        payload: {
          'Page name': document.title,
          URL: window.location.pathname,
          'CTA location': 'Help modal',
          'CTA destination': 'Call us',
          'CTA text': 'Call us',
          'CTA URL': `tel:${phoneNumberLink}`,
        },
      }),
    );
  };

  const handleEmailTracking = () => {
    Amplitude.track(
      Events.introducerPortal.introducerPortalEventCreator({
        event: AMPLITUDE_EVENTS.contactLinkClicked,
        locale,
        payload: {
          'Page name': document.title,
          URL: window.location.pathname,
          'CTA location': 'Help modal',
          'CTA destination': 'Email us',
          'CTA text': 'Email us',
          'CTA URL': `mailto:${
            isAccountant ? t`accountants@iwoca.co.uk` : t`brokers@iwoca.co.uk`
          }`,
        },
      }),
    );
  };

  return (
    <Modal
      closeIcon
      headerBar
      isOpen={showModal}
      onClose={closeModal}
      title={t`Need help?`}
    >
      <div className={styles.mainContent}>
        <picture>
          <img
            src={`${ASSET_PREFIX}/personWearingHeadset.jpg`}
            className={styles.image}
            alt={t`Person wearing a headset`}
          />
        </picture>
        <div className={styles.informationContent}>
          <p className={styles.callUsText}>
            {t`Call us Monday to Friday (9am - 6pm)`}
          </p>
          <p className={styles.phoneNumber}>
            <a
              href={`tel:${phoneNumberLink}`}
              onClick={handlePhoneNumberTracking}
              data-testid="call-link"
            >
              {phoneNumber}
            </a>
          </p>
          <p className={styles.emailUsText}>{t`Email us at`}</p>
          <div>
            {isAccountant ? (
              <a
                href="mailto:accountants@iwoca.co.uk"
                onClick={handleEmailTracking}
                data-testid="email-link"
              >
                {t`accountants@iwoca.co.uk`}
              </a>
            ) : (
              <a
                href={`mailto:${t`brokers@iwoca.co.uk`}`}
                onClick={handleEmailTracking}
                data-testid="email-link"
              >
                {t`brokers@iwoca.co.uk`}
              </a>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
